/**
 * Borrower fields that are another way of reaching the same person as the field they hang off,
 * rather than a subject of their own: a second address for Email, the three handset fields for
 * Phone.
 *
 * None of them is ever written into a saved section - admin places `email` and `phone`, and these
 * are simply never listed - so left to the normal rules they all drop to the unsectioned block at
 * the foot of the form, a screen away from the field they belong with. On a loan whose Cell Phone
 * and Phone hold different numbers that reads as the platform only offering the one, which is
 * what Longhorn reported on 311996: the Loan Overview banner prints Cell Phone and Work Phone,
 * and neither appeared beside the Phone box they were editing.
 */
export const fieldCompanions: Record<string, string[]> = {
  email: ['additionalEmails'],
  phone: ['cellPhone', 'homePhone', 'workPhone'],
}

export const companionKeys = Object.values(fieldCompanions).flat()

/**
 * Orders one block's field keys so every companion is drawn immediately after its host.
 *
 * A companion is passed through whether or not the tenant has it switched on: the caller's
 * `renderInput` drops a hidden field, so the ones that are on stay together rather than closing
 * up around a gap. A companion whose host is absent from `fields` is dropped, which is what keeps
 * it from being drawn twice when the host lives in another section.
 */
export const withCompanions = (fields: string[]) => {
  const rest = fields.filter((key) => !companionKeys.includes(key))
  return rest.flatMap((key) => [key, ...(fieldCompanions[key] || [])])
}

/**
 * The companions that a section already draws beside their host, and so must not appear again in
 * the unsectioned block at the foot of the form.
 */
export const companionsDrawnInSections = (sections: { fields: string[] }[]) =>
  Object.keys(fieldCompanions)
    .filter((host) => sections.some((section) => section.fields.includes(host)))
    .flatMap((host) => fieldCompanions[host])
